/**
 * Navigation utility functions
 * Provides consistent back navigation behavior across the app
 */

/**
 * Get the returnTo path from location state or query params
 * 
 * Priority order:
 * 1. location.state.returnTo (router state - survives navigation but not refresh)
 * 2. ?returnTo= query param (survives refresh/new tab)
 * 3. fallback path
 * 
 * Security: Only allows internal routes that start with "/" (prevents open redirects)
 * 
 * @param {Object} location - React Router location object from useLocation()
 * @param {string} fallback - Fallback path if no returnTo found (default: "/")
 * @returns {string} The returnTo path to navigate to
 * 
 * @example
 * import { useLocation } from 'react-router-dom';
 * import { getReturnTo } from '../utils/navigation';
 * 
 * const location = useLocation();
 * const returnTo = getReturnTo(location, "/");
 */
export function getReturnTo(location, fallback = "/") {
  // Priority 1: Check router state (survives navigation but not refresh)
  const returnToState = location?.state?.returnTo;
  if (returnToState && typeof returnToState === 'string' && returnToState.startsWith('/')) {
    return returnToState;
  }
  
  // Priority 2: Check query param ?returnTo= (survives refresh/new tab)
  const params = new URLSearchParams(location?.search || "");
  const returnToQuery = params.get("returnTo");
  if (returnToQuery && typeof returnToQuery === 'string' && returnToQuery.startsWith('/')) {
    return returnToQuery;
  }
  
  // Fallback to default path
  return fallback;
}

/**
 * Handle back navigation with deterministic returnTo mechanism
 * 
 * Priority order:
 * 1. location.state.from (router state - survives navigation but not refresh)
 * 2. ?from= query param (survives refresh/new tab)
 * 3. navigate(-1) if history exists
 * 4. fallbackTo path
 * 
 * @param {Function} navigate - React Router navigate function from useNavigate()
 * @param {Object} location - React Router location object from useLocation()
 * @param {string} fallbackTo - Fallback path if no returnTo context and no history (default: "/activities")
 * 
 * @example
 * import { useNavigate, useLocation } from 'react-router-dom';
 * import { handleBackNavigation } from '../utils/navigation';
 * 
 * const navigate = useNavigate();
 * const location = useLocation();
 * <button onClick={() => handleBackNavigation(navigate, location)}>Back</button>
 */
export const handleBackNavigation = (navigate, location, fallbackTo = "/activities") => {
  // Priority 1: Check router state (survives navigation but not refresh)
  const fromState = location?.state?.from;
  if (fromState) {
    console.log('[Navigation] Using state.from:', fromState);
    return navigate(fromState);
  }
  
  // Priority 2: Check query param ?from= (survives refresh/new tab)
  const params = new URLSearchParams(location?.search || "");
  const fromQuery = params.get("from");
  if (fromQuery) {
    console.log('[Navigation] Using query param from:', fromQuery);
    return navigate(fromQuery);
  }
  
  // Priority 3: Use browser history if available
  if (window.history.length > 1) {
    console.log('[Navigation] Using browser history (navigate(-1))');
    return navigate(-1);
  }
  
  // Priority 4: Fallback to default path
  console.log('[Navigation] Using fallback:', fallbackTo);
  return navigate(fallbackTo);
};
